import { FC, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import {
  AdditionalServiceForm,
  IAdditionalServiceForm,
} from 'entities/appeal/ui/form/appeal-form-service'
import { FeedBackBanner } from 'entities/appeal/ui/form/feed-back-form/components/feed-back-banner'

import { AppealApi } from 'shared/api/Appeal.ts'
import {
  CreateAppealFormData,
  CreateAppealFormDataRequest,
  KeyValue,
} from 'shared/api/AppealModel.ts'
import { MyTicketItem } from 'shared/api/MyTicketsModel.ts'
import { TYPE_APPELS } from 'shared/constants'
import { convertFilesToBase64 } from 'shared/lib//convertImageToBase64'
import { getErrorMessage } from 'shared/lib/errorText'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'

import styles from './styles.module.scss'

interface Props {
  ticket?: MyTicketItem
}

enum RESULT_FORM {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

const AdditionalServicesPage: FC<Props> = ({ ticket }) => {
  const { t } = useTranslation()
  const [params] = useSearchParams()

  const { closeDialog, openDialog } = useDialogBottomSheet()

  const paramBilling = params.get('billing_number')

  const [topics, setTopics] = useState<KeyValue[]>()
  const [loading, setLoading] = useState<boolean>(false)

  const getAppealTopics = async () => {
    const { data: AppealsSubjects } = await AppealApi.getAppealsTopics(
      String(paramBilling),
    )

    setTopics(AppealsSubjects?.data?.[0]?.additional_service_subjects)
  }

  const onCreateAppealService = async (data: IAdditionalServiceForm) => {
    setLoading(true)

    const createAppealData: CreateAppealFormData[] = data?.forms?.map(
      (dataItem) => {
        const subjectSelected = topics?.filter(
          (topic) => topic.key === dataItem?.topic,
        )[0]

        return {
          topic: TYPE_APPELS.ADDITIONAL_SERVICE,
          email: data?.email,
          billing_number: ticket?.billing_number || 0,
          subject: subjectSelected?.value,
          avia_topic_id: String(subjectSelected!.key),
          details: dataItem?.details,
          images: dataItem?.files || [],
          contact_phone_number: `${data?.phone?.code}${data?.phone?.phone}`,
          appeals_detail: {
            passengers: dataItem?.passengers?.map((item) => ({
              passenger_key: item.value,
            })),
          },
        }
      },
    )

    if (!createAppealData?.length) return null

    onCreateAppealServiceReqeust(createAppealData)
  }

  const onCreateAppealServiceReqeust = async (
    data: CreateAppealFormData[],
    index: number = 0,
  ) => {
    if (data[index] === undefined) {
      setLoading(false)
      onShowResults(RESULT_FORM.SUCCESS)

      return
    }

    const preparedData: CreateAppealFormDataRequest = {
      ...data[index],
      images:
        data[index].images !== undefined
          ? await convertFilesToBase64(data[index].images)
          : [],
    }

    try {
      await AppealApi.onCreateAppeal(preparedData)

      onCreateAppealServiceReqeust(data, index + 1)
    } catch (err) {
      const errorText = getErrorMessage(err)

      setLoading(false)
      onShowResults(RESULT_FORM.ERROR, errorText)
    }
  }

  const onShowResults = (result: RESULT_FORM, textError?: string) => {
    openDialog({
      isDialog: false,
      child: (
        <PopupContent
          button={t('continue')}
          title={
            result === RESULT_FORM.ERROR
              ? t('error')
              : t('feed_back.purchase_title')
          }
          description={
            textError?.length
              ? textError
              : result === RESULT_FORM.ERROR
                ? t('error_des')
                : t('feed_back.purchase_description')
          }
          onClick={closeDialog}
        />
      ),
    })
  }

  useEffect(() => {
    getAppealTopics()
  }, [])

  const options = topics?.map((option) => ({
    name: option?.value,
    option: option?.key,
  }))

  return (
    <div className={styles.container}>
      <HeaderGoBack
        title={t('additional_service.title')}
        buttonWhite
      />

      {loading ? (
        <Loader marginTop={200} />
      ) : (
        <div className={styles.box}>
          <FeedBackBanner />
          <AdditionalServiceForm
            ticket={ticket}
            optionServices={options}
            onCreateService={onCreateAppealService}
          />
        </div>
      )}
    </div>
  )
}

export default AdditionalServicesPage
